import axios from "axios";
import store from "@/store";
import router from "@/router";

// Constants
const localBaseUrl = `http://${window.location.hostname}:5001`;
const API_BASE_URL = process.env.NODE_ENV === "local" ? localBaseUrl : process.env.VUE_APP_API_BASE_URL;
const JWT_ERROR_MESSAGES = [
  "jwt must be provided",
  "invalid signature",
  "Invalid request. Token is not in store"
];

const JWT_EXPIRED_MESSAGES = [
  "jwt expired"
];

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(axios(config));
    }
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  const currentPath = window.location.pathname;
  if (currentPath !== "/login" && currentPath !== "/oauth2") {
    console.log("Redirecting to login from:", currentPath);
    router.push({ path: "/login", query: { from: currentPath } }).catch(navErr => {
      if (navErr.name !== 'NavigationDuplicated') {
        console.error(navErr);
      }
    });
  }
};

// Axios configuration
const configureAxios = () => {
  axios.defaults.baseURL = API_BASE_URL;
  axios.defaults.withCredentials = true;
  axios.defaults.headers.common["Content-Type"] = "application/json";
  axios.defaults.headers.common["Accept-Language"] = "en-US";

  // Request interceptor
  axios.interceptors.request.use(
    req => {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        req.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      if (/\.s3[.-]/.test(req.url)) {
        delete req.headers.Authorization;
      }
      return req;
    },
    err => {
      return Promise.reject(err);
    }
  );

  // Response interceptor
  axios.interceptors.response.use(
    res => {
      return res;
    },
    async err => {
      const originalConfig = err.config;
      if (
        originalConfig.url !== `/api/login` &&
        originalConfig.url !== `/api/token` && // Don't retry token refresh requests
        err.response &&
        (originalConfig.baseURL.includes(API_BASE_URL) ||
          originalConfig.baseURL.includes(localBaseUrl))
      ) {
        const { response } = err;

        // Handle non-expired JWT errors (should redirect to login immediately)
        if (JWT_ERROR_MESSAGES.includes(response.data?.errorMessage?.message)) {
          const currentPath = window.location.pathname;
          if (currentPath === "/oauth2" || currentPath === "/login") return Promise.reject(err);

          redirectToLogin();
          return Promise.reject(err);
        }

        // Handle 401 errors or expired JWT (should attempt token refresh)
        if ((response.status === 401 || JWT_EXPIRED_MESSAGES.includes(response.data?.errorMessage?.message)) && !originalConfig._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject, config: originalConfig });
            });
          }

          originalConfig._retry = true;
          isRefreshing = true;

          try {
            console.log("Attempting token refresh...");
            await store.dispatch("moduleAuth/refreshToken");
            console.log("Token refresh successful, retrying original request");
            processQueue(null);
            return axios(originalConfig);
          } catch (refreshError) {
            // Refresh failed - clear tokens and redirect
            console.log("Token refresh failed:", refreshError);
            localStorage.removeItem("access_token");
            processQueue(refreshError);
            redirectToLogin();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      return Promise.reject(err);
    }
  );
};

export default configureAxios;
export { axios };